// Reusable-slide catalogue contract for @rdx/deck-system.
//
// A SlideTemplate is a SINGLE reusable Slide (see ./deck-model.ts `Slide`)
// wrapped with picker/retrieval metadata. The catalogue lets a future deck drop
// a canonical Workplace-by-Ricoh slide in verbatim rather than re-create it, and
// gives the AI generation path a described inventory of product-canon slides.
//
// Phase 1 (now): every template is a BUILTIN — the canonical product-canon
// slides lifted from the recreated RCL deck. The catalogue JSON is the data;
// this file is the typed contract + accessors.
//
// Phase 2 (reserved): the `org` / `shared` scopes back a save/share feature
// where a user promotes one of their own slides into a reusable template. The
// `ownerId` / `orgId` / `createdAt` / `updatedAt` fields below are documented
// now and intentionally left UNSET on builtins.
//
// DUAL-MAINTENANCE: the catalogue data lives in
//   web/src/lib/slide-templates.builtin.json          (this consumer — FE)
//   server/internal/slidetemplates/builtin.json        (the Go server — go:embed)
// The two JSON files MUST stay byte-identical in content. This mirrors the
// existing BLOCK_TYPES (deck-model.ts) <-> allowedBlockTypes (deckgen.go) split.
// Phase 2 will unify them via a generator; until then, edit both.
//
// British spelling throughout. No emojis.

import type { Slide } from './deck-model';
import builtinCatalogue from './slide-templates.builtin.json';

/** Catalogue scope. Phase 1 only ever emits "builtin"; "org" and "shared" are
 *  reserved for the future save/share feature and carry no builtins. */
export type SlideTemplateScope = 'builtin' | 'org' | 'shared';

/** A reusable slide plus the metadata that drives the picker AND the AI
 *  catalogue. The `slide` field is exactly one DeckModel `Slide`. */
export interface SlideTemplate {
  /** Visibility/origin. Phase 1 = "builtin". */
  scope: SlideTemplateScope;
  /** Stable kebab key, e.g. "wbr-six-apps". Primary key for getBuiltinTemplate. */
  id: string;
  /** Picker label, e.g. "Six apps, one switcher". */
  name: string;
  /** One-line when-to-use. Drives BOTH the picker subtitle AND the AI
   *  catalogue retrieval blurb. */
  description: string;
  /** Picker grouping, e.g. "Workplace by Ricoh". */
  category: string;
  /** Retrieval / match hints for the AI catalogue and picker search. */
  tags: string[];
  /** True for the canonical product-canon slides that describe the product
   *  itself — lets the AI path bias towards them when a deck is about the
   *  Workplace by Ricoh platform. */
  describesWorkplace: boolean;
  /** The canonical Slide JSON — copied verbatim from the source deck, with a
   *  generic reusable label. */
  slide: Slide;

  // ── Phase 2 (reserved) — documented, UNSET on builtins ──────────────────
  /** Owner user id (org/shared scopes). */
  ownerId?: string;
  /** Owning org id (org/shared scopes). */
  orgId?: string;
  /** ISO-8601 creation timestamp (org/shared scopes). */
  createdAt?: string;
  /** ISO-8601 last-update timestamp (org/shared scopes). */
  updatedAt?: string;
}

// The JSON import is typed structurally by Vite; cast through `unknown` to the
// contract so consumers get a fully-typed SlideTemplate[] regardless of whether
// resolveJsonModule is enabled. The catalogue is validated at build/test time
// against this shape and against the DeckModel block-type invariant.
const BUILTIN_TEMPLATES = builtinCatalogue as unknown as SlideTemplate[];

/** Every builtin reusable slide, typed. The returned array is the live
 *  catalogue order (catalogue authoring order). */
export function getBuiltinTemplates(): SlideTemplate[] {
  return BUILTIN_TEMPLATES;
}

/** A single builtin by stable id, or undefined if no such id exists. */
export function getBuiltinTemplate(id: string): SlideTemplate | undefined {
  return BUILTIN_TEMPLATES.find((t) => t.id === id);
}
